/**
 * The dispatch board's arithmetic (M1-09), with no React in it.
 *
 * Everything here is a pure function over instants and minute counts, so the
 * awkward parts — a business that closes before its last job finishes, a
 * technician double-booked, a day that is 23 hours long because the clocks
 * moved — are unit-testable without rendering anything. jsdom cannot measure
 * layout, so the board's correctness has to live somewhere jsdom *can* check;
 * this is that somewhere.
 */
import type { CivilDate } from './timezone';
import { addDays, civilDayOfWeek, parseWallClock, zonedTimeToInstant } from './timezone';

/** Half an hour. Fine enough for a plumbing call, coarse enough to stay readable. */
export const SLOT_MINUTES = 30;

const MS_PER_MINUTE = 60_000;

/** One day's hours as `GET /api/v1/account` returns them. */
export interface DayHours {
  day: string;
  open: string;
  close: string;
}

/** The subset of a job this module needs. Deliberately not the whole DTO. */
export interface SchedulableJob {
  id: string;
  assignedUserId: string | null;
  scheduledStart: string | null;
  scheduledEnd: string | null;
}

const WEEKDAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

/**
 * The range the board falls back to when the business has no hours recorded
 * for that weekday — its weekend, or an account nobody has configured yet.
 * Wide enough to hold an ordinary day's work, and always widened further to
 * cover whatever is actually scheduled.
 */
export const FALLBACK_OPEN_MINUTE = 7 * 60;
export const FALLBACK_CLOSE_MINUTE = 19 * 60;

export interface BoardDay {
  date: CivilDate;
  timeZone: string;
  /** Midnight, on the account's clock. */
  dayStart: Date;
  /** The following midnight — 23, 24 or 25 hours later, depending on the clocks. */
  dayEnd: Date;
}

/** The instants bounding a calendar date, on the account's own clock. */
export function boardDay(date: CivilDate, timeZone: string): BoardDay {
  return {
    date,
    timeZone,
    dayStart: zonedTimeToInstant(date, 0, 0, timeZone),
    dayEnd: zonedTimeToInstant(addDays(date, 1), 0, 0, timeZone),
  };
}

/**
 * The seven days of the week containing `date`, Sunday first.
 *
 * Sunday because the account's own locale (America/Chicago, and every other US
 * zone) puts it there on a wall calendar. `Intl.Locale.getWeekInfo()` would
 * derive it, but its support is uneven enough that the fallback branch is what
 * CI would actually exercise — an untested feature dressed as a tested one. A
 * per-account week start is a settings question nobody has asked, and this is
 * the one place it would change.
 *
 * Each day is built through `boardDay`, so a week containing a clock change
 * holds one 23- or 25-hour day and six ordinary ones.
 */
export function boardWeek(date: CivilDate, timeZone: string): BoardWeek {
  const start = startOfWeek(date);
  const days = Array.from({ length: 7 }, (_, i) => boardDay(addDays(start, i), timeZone));
  return {
    startDate: start,
    days,
    // Non-null: the array is built with exactly seven entries just above.
    weekStart: days[0]!.dayStart,
    weekEnd: days[6]!.dayEnd,
  };
}

export interface BoardWeek {
  startDate: CivilDate;
  days: BoardDay[];
  weekStart: Date;
  weekEnd: Date;
}

/**
 * The Sunday beginning the week that contains `date`.
 *
 * The single place the week-start rule is written down. Anything that needs to
 * ask "are these two dates in the same week?" — the navigation's "this week"
 * button, for one — compares `startOfWeek` values rather than re-deriving the
 * rule, so changing it later is one edit rather than a hunt.
 *
 * Purely civil arithmetic: no zone and no instants are involved, so it cannot
 * pick up the off-by-one that reading a weekday off a midnight *instant*
 * would in any zone whose local midnight falls on a different UTC date.
 */
export function startOfWeek(date: CivilDate): CivilDate {
  return addDays(date, -civilDayOfWeek(date));
}

/** Whether two dates fall in the same week. */
export function isSameWeek(a: CivilDate, b: CivilDate): boolean {
  const first = startOfWeek(a);
  const second = startOfWeek(b);
  return first.year === second.year && first.month === second.month && first.day === second.day;
}

/**
 * Whether a job runs during a given day.
 *
 * The board fetches a whole week in one request and then splits it back into
 * days here, so this predicate has to agree exactly with the one the API
 * applied — half-open on both ends for a job with an end, and "starts within"
 * for a job that is only a point in time. If the two ever disagree, a job is
 * fetched and then dropped, and the week is quietly missing work that the day
 * view shows.
 */
export function occursOnDay(job: SchedulableJob, { dayStart, dayEnd }: BoardDay): boolean {
  if (!job.scheduledStart) {
    return false;
  }
  const start = new Date(job.scheduledStart).getTime();
  if (Number.isNaN(start)) {
    return false;
  }
  const rawEnd = job.scheduledEnd ? new Date(job.scheduledEnd).getTime() : Number.NaN;
  if (start >= dayEnd.getTime()) {
    return false;
  }
  return Number.isNaN(rawEnd) ? start >= dayStart.getTime() : rawEnd > dayStart.getTime();
}

/** The business's hours for the weekday `date` falls on, if it has any. */
export function hoursForDate(days: readonly DayHours[], date: CivilDate): DayHours | undefined {
  const name = WEEKDAY_NAMES[civilDayOfWeek(date)];
  return days.find((d) => d.day === name);
}

export interface VisibleRange {
  /** Minutes after `dayStart` at which the grid begins. */
  startMinute: number;
  /** Minutes after `dayStart` at which it ends. Always a whole number of slots past the start. */
  endMinute: number;
  /** True when the business records no hours for this weekday — a closed day. */
  isClosedDay: boolean;
  /** The business's own open/close, when it has them, for flagging work outside them. */
  openMinute: number | null;
  closeMinute: number | null;
}

/**
 * How much of the day the grid draws.
 *
 * Business hours set the range (the AC), but they never *hide* a job. A job at
 * 05:00 on a business that opens at seven is precisely the job a dispatcher
 * needs to see, and a board that quietly omits it is worse than one that
 * scrolls: the count in the header would say four jobs and the grid would show
 * three, with nothing to explain the difference. So the range is business
 * hours, widened to contain everything scheduled, and rounded out to whole
 * slots.
 *
 * `openMinute`/`closeMinute` are returned alongside so the board can shade the
 * widened part as outside hours rather than pretending it is a normal morning
 * — which is also what M1-12 flags against.
 */
/**
 * Converts a wall-clock time on this day into minutes elapsed since its local
 * midnight.
 *
 * Business hours are wall-clock; everything else on the board is elapsed time.
 * They are the same number on 363 days a year and differ by an hour on the
 * other two, so the conversion goes through the zone rather than assuming they
 * agree — otherwise the grid on a changeover day starts an hour away from the
 * time printed on its own first row label.
 */
function elapsedMinutesOf(minutesPastMidnight: number, day: BoardDay): number {
  return (
    (zonedTimeToInstant(
      day.date,
      Math.floor(minutesPastMidnight / 60),
      minutesPastMidnight % 60,
      day.timeZone,
    ).getTime() -
      day.dayStart.getTime()) /
    MS_PER_MINUTE
  );
}

/**
 * When the business is open on this day, in minutes after its local midnight,
 * or null if it is closed (or its hours are unusable).
 *
 * One definition, used by the grid that draws the hours and by the check that
 * flags work outside them (M1-12). Two copies of this would eventually shade
 * one range and flag another.
 */
export function businessWindow(
  hours: DayHours | undefined,
  day: BoardDay,
): { openMinute: number; closeMinute: number } | null {
  const parsedOpen = hours ? parseWallClock(hours.open) : null;
  const parsedClose = hours ? parseWallClock(hours.close) : null;
  // A malformed or inverted "HH:mm" is treated as no hours at all rather than
  // being guessed at. parseWallClock already refuses anything unparseable.
  if (parsedOpen === null || parsedClose === null || parsedClose <= parsedOpen) {
    return null;
  }
  return {
    openMinute: elapsedMinutesOf(parsedOpen, day),
    closeMinute: elapsedMinutesOf(parsedClose, day),
  };
}

export function visibleRange(
  hours: DayHours | undefined,
  jobs: readonly SchedulableJob[],
  day: BoardDay,
): VisibleRange {
  const { dayStart, dayEnd } = day;
  const window = businessWindow(hours, day);
  const usable = window !== null;

  const openMinute = window?.openMinute ?? null;
  const closeMinute = window?.closeMinute ?? null;

  let startMinute = openMinute ?? elapsedMinutesOf(FALLBACK_OPEN_MINUTE, day);
  let endMinute = closeMinute ?? elapsedMinutesOf(FALLBACK_CLOSE_MINUTE, day);

  const dayLengthMinutes = (dayEnd.getTime() - dayStart.getTime()) / MS_PER_MINUTE;

  for (const job of jobs) {
    const span = jobSpan(job, dayStart, dayEnd);
    if (!span) {
      continue;
    }
    startMinute = Math.min(startMinute, span.startMinute);
    endMinute = Math.max(endMinute, span.endMinute);
  }

  startMinute = Math.max(0, Math.floor(startMinute / SLOT_MINUTES) * SLOT_MINUTES);
  endMinute = Math.min(
    dayLengthMinutes,
    Math.ceil(endMinute / SLOT_MINUTES) * SLOT_MINUTES,
  );

  // Rounding can only ever widen, but a business configured 08:00-08:10 would
  // still round to a single slot; guarantee at least one so the grid is never
  // zero rows tall.
  if (endMinute <= startMinute) {
    endMinute = startMinute + SLOT_MINUTES;
  }

  return {
    startMinute,
    endMinute,
    isClosedDay: !usable,
    openMinute,
    closeMinute,
  };
}

export interface JobSpan {
  /** Minutes after `dayStart`. Clamped into the day, so an overnight job starts at 0. */
  startMinute: number;
  endMinute: number;
}

/**
 * Where a job sits within the day, in minutes after midnight.
 *
 * Measured as elapsed time from `dayStart` rather than by reading the job's
 * wall-clock hour, which is what makes a 25-hour day lay out correctly: the
 * grid's slots are also elapsed time, so the two cannot disagree.
 *
 * A job with no end is given one slot of height — a zero-height card is
 * unclickable and undraggable, and M1-11 has to be able to grab it.
 */
export function jobSpan(
  job: SchedulableJob,
  dayStart: Date,
  dayEnd: Date,
): JobSpan | null {
  if (!job.scheduledStart) {
    return null;
  }
  const start = new Date(job.scheduledStart).getTime();
  if (Number.isNaN(start)) {
    return null;
  }
  const rawEnd = job.scheduledEnd ? new Date(job.scheduledEnd).getTime() : Number.NaN;
  const end = Number.isNaN(rawEnd) ? start + SLOT_MINUTES * MS_PER_MINUTE : rawEnd;

  const dayStartMs = dayStart.getTime();
  const dayEndMs = dayEnd.getTime();
  // Clamped to the day being drawn: an overnight job is shown running from the
  // top of the board, not at a negative offset off the top of the screen.
  const startMinute = Math.max(0, (Math.max(start, dayStartMs) - dayStartMs) / MS_PER_MINUTE);
  const endMinute = Math.max(
    startMinute + 1,
    (Math.min(end, dayEndMs) - dayStartMs) / MS_PER_MINUTE,
  );

  return { startMinute, endMinute };
}

export interface PlacedJob<TJob> {
  job: TJob;
  span: JobSpan;
  /** Which sub-column within the technician's column this card sits in. */
  lane: number;
  /** How many sub-columns that cluster of overlapping jobs needs. */
  laneCount: number;
}

/**
 * Lays out one technician's jobs, splitting the column between jobs that
 * overlap in time.
 *
 * Without this, a double-booked technician's second job is drawn exactly on
 * top of the first and is invisible — which is the opposite of what M1-12 is
 * for. Overlapping jobs are grouped into clusters, and every card in a cluster
 * is narrowed to the same width so the column stays a readable grid rather
 * than a cascade.
 *
 * Greedy, first-free-lane assignment over jobs sorted by start. That is not
 * the minimum possible number of lanes in every pathological case, but it is
 * stable, obvious, and O(n) in the number of jobs a technician can physically
 * do in a day.
 */
export function packLanes<TJob extends SchedulableJob>(
  jobs: readonly TJob[],
  dayStart: Date,
  dayEnd: Date,
): PlacedJob<TJob>[] {
  const placeable = jobs
    .map((job) => ({ job, span: jobSpan(job, dayStart, dayEnd) }))
    .filter((entry): entry is { job: TJob; span: JobSpan } => entry.span !== null)
    .sort((a, b) => a.span.startMinute - b.span.startMinute || a.job.id.localeCompare(b.job.id));

  const placed: PlacedJob<TJob>[] = [];
  // The jobs of the current overlap cluster, and when each lane frees up.
  let cluster: PlacedJob<TJob>[] = [];
  let laneEnds: number[] = [];

  const closeCluster = () => {
    for (const entry of cluster) {
      entry.laneCount = laneEnds.length;
    }
    placed.push(...cluster);
    cluster = [];
    laneEnds = [];
  };

  for (const { job, span } of placeable) {
    // Nothing in the cluster is still running: it is finished, and this job
    // starts a new one at full width.
    if (laneEnds.length > 0 && laneEnds.every((end) => end <= span.startMinute)) {
      closeCluster();
    }

    let lane = laneEnds.findIndex((end) => end <= span.startMinute);
    if (lane === -1) {
      lane = laneEnds.length;
    }
    laneEnds[lane] = span.endMinute;
    cluster.push({ job, span, lane, laneCount: laneEnds.length });
  }
  closeCluster();

  return placed;
}

/**
 * The slot boundaries the grid draws, as instants.
 *
 * Instants rather than "08:00, 08:30, …" strings so the labels stay honest on
 * the two days a year the clocks move: on the spring changeover the labels run
 * 01:30 then 03:00, because that is what the technicians' clocks do.
 */
export function slotInstants(dayStart: Date, range: VisibleRange): Date[] {
  const slots: Date[] = [];
  for (let minute = range.startMinute; minute < range.endMinute; minute += SLOT_MINUTES) {
    slots.push(new Date(dayStart.getTime() + minute * MS_PER_MINUTE));
  }
  return slots;
}

/** How many slots tall the grid is. */
export function slotCount(range: VisibleRange): number {
  return Math.max(1, Math.round((range.endMinute - range.startMinute) / SLOT_MINUTES));
}

/**
 * A span expressed as CSS grid rows (1-based, end-exclusive), which is how the
 * board actually draws a card. Clamped into the visible range so a job the
 * range could not be widened to cover still lands somewhere real.
 */
export function gridRows(span: JobSpan, range: VisibleRange): { start: number; end: number } {
  const total = slotCount(range);
  const rawStart = (span.startMinute - range.startMinute) / SLOT_MINUTES;
  const rawEnd = (span.endMinute - range.startMinute) / SLOT_MINUTES;
  const start = Math.min(Math.max(0, Math.floor(rawStart)), total - 1);
  const end = Math.min(Math.max(start + 1, Math.ceil(rawEnd)), total);
  return { start: start + 1, end: end + 1 };
}
